import debounce from 'lodash/debounce'

/**
 * Simple Class which allows to execute a callback function when the window is resized.
 * The callback function will receive an object with the following properties:
 * - threshold: an object with the x and y values of the threshold
 * - changedAxis: an array with the axis that have changed
 *
 * The options of this class are:
 * - delay: the debounce delay
 * - resizeThreshold: the minimum change in pixels to trigger the callback
 * - detectAxis: an array with the axis to detect changes
 *
 * Example usage:
 * new ResizeCallback((data) => {
 *         console.log(data)
 * }, {
 *      delay: 100,
 *      resizeThreshold: 0,
 *      detectAxis: ['x', 'y']
 * })
 *
 */
class ResizeCallback {
	// Ensures that query is a string and callback is a function
	constructor(callback, options) {
		this.callback = callback
		this.options = Object.assign(
			{
				delay: 100,
				resizeThreshold: 0, // in pixels
				detectAxis: ['x', 'y'],
			},
			options,
		)

		this.lastDimensions = {
			width: window.innerWidth,
			height: window.innerHeight,
		}

		this.debouncedResize = debounce(this.onResize.bind(this), this.options.delay)

		window.addEventListener('resize', this.debouncedResize)
	}
	onResize() {
		let currentDimensions = {
			width: window.innerWidth,
			height: window.innerHeight,
		}

		let changedAxis = []

		if (this.lastDimensions.width !== currentDimensions.width) {
			changedAxis.push('x')
		}
		if (this.lastDimensions.height !== currentDimensions.height) {
			changedAxis.push('y')
		}

		// check if any value in the changedAxis array is in the detectAxis array
		if (changedAxis.some((axis) => this.options.detectAxis.includes(axis))) {
			const changeValues = {
				x: Math.abs(this.lastDimensions.width - currentDimensions.width),
				y: Math.abs(this.lastDimensions.height - currentDimensions.height),
			}

			if (this.options.resizeThreshold > 0) {
				// check if any of the axis has changed more than the threshold

				if (changeValues.x < this.options.resizeThreshold && changeValues.y < this.options.resizeThreshold) {
					// threshold not reached
					this.lastDimensions = currentDimensions
					return
				}
			}

			let callbackObj = {
				threshold: changeValues,
				changedAxis: changedAxis,
			}

			this.callback(callbackObj)
			this.lastDimensions = currentDimensions
		}
	}
	destroy() {
		window.removeEventListener('resize', this.debouncedResize)
		if (this.debouncedResize) {
			this.debouncedResize.cancel()
			this.debouncedResize = null
		}
	}
}

export { ResizeCallback }
